import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Collapse, IconButton } from '@material-ui/core';

import { loadEmployeeGroups } from '../../employee-groups/actions/load-employee-groups';
import { employeeGroupsForOrgIdSelector } from '../../employee-groups/employee-group-selectors';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../employees/actions/load-org-employees-by-status';
import { employeesForOrgIdSelector } from '../../reducers/employee-selectors';
import { orgSelector, orgsForAccountId } from '../../reducers/org-selectors';

import { EmployeeChangeLogDetails } from '../../employee-change-logs/components/employee-change-log-details';
import { EMPLOYEE_CHANGE_LOG_CHANGE_TYPE } from '../../typescript-types/employee-change-log';
import { employeeRoute } from '../../web-routes';

import { HrisImportEmployeeChangeLogRow } from './hris-employee-diff-table';

import { DOMAIN } from '../../config/config';
import { getValueChanges } from '../../employee-change-logs/util/get-value-changes';
import { combineWords, humanizeCamelCaseString } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';

type HrisEmployeeDiffTableChangesCellProps = {
  hrisImportEmployeeChangeLog: HrisImportEmployeeChangeLogRow;
};

export const HrisEmployeeDiffTableChangesCell = ({
  hrisImportEmployeeChangeLog,
}: HrisEmployeeDiffTableChangesCellProps) => {
  const {
    employeeId,
    employeeChangeLogId,
    employeeChanges,
    personChanges,
    orgId,
    changeType,
    groupChanges,
    supervisorChanges,
    subordinateChanges,
  } = hrisImportEmployeeChangeLog;
  const [open, setOpen] = useState(false);

  const org = useSelector((state: any) => orgSelector(state, orgId));
  const orgsInAccount =
    useSelector((state: any) => (org ? orgsForAccountId(state, org.accountId) : [])) ?? [];
  const employeesInOrg = useSelector((state: any) => employeesForOrgIdSelector(state, orgId));
  const employeeGroupsInOrg =
    useSelector((state: any) => employeeGroupsForOrgIdSelector(state, orgId)) ?? [];

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadEmployeeGroups(orgId));
    dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.CURRENT));
    dispatch(loadOrgEmployeesByStatus(orgId, EMPLOYEE_STATUS.FORMER));
  }, [dispatch, orgId]);

  if (changeType === EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.CREATE) {
    return (
      <div>
        <a
          href={`${DOMAIN}${employeeRoute(orgId, employeeId)}`}
          className="typo-link"
          target="_blank"
          rel="noopener noreferrer"
          style={{ width: '24px', height: '24px' }}
          aria-label="Open"
        >
          View new employee profile
        </a>
      </div>
    );
  }

  const valueChanges = getValueChanges(employeeChanges ?? {}, orgsInAccount)
    .concat(getValueChanges(personChanges ?? {}, orgsInAccount))
    .map(({ field }) => humanizeCamelCaseString(field));

  if (employeeChanges?.integrationId) {
    valueChanges.push('Employee linked in HRIS');
  }
  if (groupChanges && Object.keys(groupChanges)) {
    valueChanges.push('Group');
  }
  if (supervisorChanges && Object.keys(supervisorChanges)) {
    valueChanges.push('Supervisor');
  }
  if (subordinateChanges && Object.keys(subordinateChanges)) {
    valueChanges.push('Subordinate');
  }
  const valueChangesToDisplay = combineWords(valueChanges);

  if (!valueChanges.length) {
    return (
      <div>
        <div>Update resulted in no useable field changes</div>
        <div className="typo-subtitle">Linking and internal data was updated</div>
      </div>
    );
  }

  return (
    <div className="u-flexColumn">
      <div className="u-flex-center">
        <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
          <MaterialDesignIcon
            iconName={open ? ICON_NAMES.KEYBOARD_ARROW_UP : ICON_NAMES.KEYBOARD_ARROW_DOWN}
          />
        </IconButton>
        <div className="u-textOverflow-ellipsis u-overflow-hidden u-whiteSpace-nowrap">
          {valueChangesToDisplay}
        </div>
      </div>
      <Collapse in={open} timeout="auto">
        <EmployeeChangeLogDetails
          changeHistoryId={employeeChangeLogId}
          employeeChanges={employeeChanges}
          personChanges={personChanges}
          orgsInAccount={orgsInAccount}
          groupChanges={groupChanges}
          employeeGroupsInOrg={employeeGroupsInOrg}
          supervisorChanges={supervisorChanges}
          subordinateChanges={subordinateChanges}
          employeesInOrg={employeesInOrg}
        />
      </Collapse>
    </div>
  );
};
